import React, { useState, useEffect } from 'react';
import { Book, TrendingUp } from 'lucide-react';
import { FaHeart, FaRegHeart, FaComment } from 'react-icons/fa';
import { BiDotsHorizontalRounded, BiTrash, BiFlag } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getProfileImageUrl } from '../utils/getProfileImageUrl';
import '../styles/Discover.css';

// Tarihleri "5 dakika önce" gibi formatlamak için yardımcı fonksiyon
const formatTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const seconds = Math.floor((now - past) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) {
        return Math.floor(interval) + " yıl önce";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
        return Math.floor(interval) + " ay önce";
    }
    interval = seconds / 86400;
    if (interval > 1) {
        return Math.floor(interval) + " gün önce";
    }
    interval = seconds / 3600;
    if (interval > 1) {
        return Math.floor(interval) + " saat önce";
    }
    interval = seconds / 60;
    if (interval > 1) {
        return Math.floor(interval) + " dakika önce";
    }
    return "az önce";
};


const Discover = () => {
    const [posts, setPosts] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [modalState, setModalState] = useState({
        showReportModal: false,
        showDeleteConfirm: false,
        showSuccessModal: false,
        reportPostId: null,
        deletePostId: null,
    });
    const [reportReason, setReportReason] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const navigate = useNavigate();
    const currentUserId = localStorage.getItem('userId');

    useEffect(() => {
        const fetchDiscoverData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const [postsResponse, authorsBooksResponse, currentUserResponse] = await Promise.all([
                    api.get('/discover/posts', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get('/discover/authors-books', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get(`/profile/${currentUserId}`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);

                if (postsResponse.data) {
                    const { posts = [] } = postsResponse.data;
                    const updatedPosts = posts.map(post => ({
                        ...post,
                        profile_image_url: getProfileImageUrl(post.profile_image_url),
                        isLiked: post.is_liked,
                        comments: post.comments.map(comment => ({
                            ...comment,
                            isLiked: comment.is_liked,
                        })),
                    }));
                    setPosts(updatedPosts);
                }

                if (authorsBooksResponse.data) {
                    const { authors = [], books = [] } = authorsBooksResponse.data;
                    setAuthors(authors);
                    setBooks(books);
                }

                if (currentUserResponse.data) {
                    const currentUserData = currentUserResponse.data.user;
                    setCurrentUser({
                        username: currentUserData.username,
                        profilePhoto: getProfileImageUrl(currentUserData.profile_image_url),
                    });
                }
            } catch (error) {
                console.error('Veri alınırken hata oluştu:', error);
                setError('Veri alınamadı. Lütfen tekrar deneyin.');
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    navigate('/login');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchDiscoverData();
    }, [navigate, currentUserId]);

    const handleLike = async (postId, commentId = null) => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        if (!token || !userId) {
            navigate('/login');
            return;
        }

        const url = commentId
            ? `/discover/comments/${commentId}/like`
            : `/discover/posts/${postId}/like`;

        try {
            await api.post(url, { userId: parseInt(userId) }, { headers: { Authorization: `Bearer ${token}` } });
            setPosts(prevPosts =>
                prevPosts.map(post => {
                    if (post.post_id === postId) {
                        if (commentId === null) {
                            return {
                                ...post,
                                likes_count: post.isLiked ? post.likes_count - 1 : post.likes_count + 1,
                                isLiked: !post.isLiked,
                            };
                        } else {
                            return {
                                ...post,
                                comments: post.comments.map(comment =>
                                    comment.comment_id === commentId
                                        ? {
                                            ...comment,
                                            likes_count: comment.isLiked
                                                ? comment.likes_count - 1
                                                : comment.likes_count + 1,
                                            isLiked: !comment.isLiked,
                                        }
                                        : comment
                                ),
                            };
                        }
                    }
                    return post;
                })
            );
        } catch (error) {
            console.error('Gönderi veya yorum beğenilirken hata:', error);
        }
    };

    const handleCommentSubmit = async (postId, e) => {
        e.preventDefault();
        const post = posts.find(p => p.post_id === postId);
        if (!post.newComment?.trim() || !currentUser) return;

        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        if (!token || !userId) {
            navigate('/login');
            return;
        }

        try {
            const response = await api.post(
                `/discover/posts/${postId}/comments`,
                { userId: parseInt(userId), postId, content: post.newComment },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newComment = {
                comment_id: response.data.commentId,
                user_id: parseInt(userId),
                profile_image_url: getProfileImageUrl(currentUser.profilePhoto),
                username: currentUser.username,
                content: post.newComment,
                created_at: new Date().toISOString(),
                likes_count: 0,
                isLiked: false,
            };

            setPosts(prevPosts =>
                prevPosts.map(p =>
                    p.post_id === postId
                        ? { ...p, comments: [...p.comments, newComment], newComment: '' }
                        : p
                )
            );
        } catch (error) {
            console.error('Yorum eklenirken hata:', error);
        }
    };

    const handleCommentChange = (postId, value) => {
        setPosts(prevPosts =>
            prevPosts.map(post =>
                post.post_id === postId ? { ...post, newComment: value } : post
            )
        );
    };

    const handleDeleteComment = async (postId, commentId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.delete(`/discover/comments/${commentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPosts(prevPosts =>
                prevPosts.map(post =>
                    post.post_id === postId
                        ? {
                            ...post,
                            comments: post.comments.filter(
                                comment => comment.comment_id !== commentId
                            ),
                        }
                        : post
                )
            );
        } catch (error) {
            console.error('Yorum silinirken hata:', error);
        }
    };

    const confirmDeletePost = postId => {
        setModalState({ ...modalState, showDeleteConfirm: true, deletePostId: postId });
    };

    const handleDeletePost = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.delete(`/discover/posts/${modalState.deletePostId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPosts(prevPosts =>
                prevPosts.filter(post => post.post_id !== modalState.deletePostId)
            );
        } catch (error) {
            console.error('Gönderi silinirken hata:', error);
        } finally {
            setModalState({ ...modalState, showDeleteConfirm: false, deletePostId: null });
        }
    };

    const handleReportPost = postId => {
        setModalState({ ...modalState, showReportModal: true, reportPostId: postId });
    };

    const sendReport = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.post(
                `/discover/posts/${modalState.reportPostId}/report`,
                { reason: reportReason, description: reportDescription },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setModalState({ ...modalState, showSuccessModal: true, showReportModal: false });
        } catch (error) {
            console.error('Gönderi şikayet edilirken hata:', error);
        } finally {
            setModalState(prev => ({ ...prev, showReportModal: false }));
        }
    };

    const handleFollowToggle = async (authorId, isFollowing) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        const url = isFollowing ? '/discover/unfollow' : '/discover/follow';
        try {
            const response = await api.post(
                url,
                { authorId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setAuthors(prevAuthors =>
                    prevAuthors.map(a =>
                        a.user_id === authorId
                            ? {
                                ...a,
                                is_following: !isFollowing,
                                followers_count: response.data.followers_count,
                            }
                            : a
                    )
                );
            }
        } catch (error) {
            console.error('Yazar takip edilirken/takipten çıkılırken hata:', error);
        }
    };

    const toggleComments = postId => {
        setPosts(prevPosts =>
            prevPosts.map(post =>
                post.post_id === postId ? { ...post, showComments: !post.showComments } : post
            )
        );
    };

    const togglePostMenu = postId => {
        setPosts(prevPosts =>
            prevPosts.map(post =>
                post.post_id === postId
                    ? { ...post, showMenu: !post.showMenu }
                    : { ...post, showMenu: false }
            )
        );
    };

    const toggleCommentMenu = (postId, commentId) => {
        setPosts(prevPosts =>
            prevPosts.map(post =>
                post.post_id === postId
                    ? {
                        ...post,
                        comments: post.comments.map(comment =>
                            comment.comment_id === commentId
                                ? { ...comment, showMenu: !comment.showMenu }
                                : { ...comment, showMenu: false }
                        ),
                    }
                    : post
            )
        );
    };

    if (isLoading) return <div className="discover-loading-state">Yükleniyor...</div>;
  if (error) return <div className="discover-error-state">{error}</div>;

  return (
    <>
      <div
        className={`discover-container ${
          modalState.showReportModal || modalState.showDeleteConfirm || modalState.showSuccessModal
            ? 'discover-modal-open'
            : ''
        }`}
      >
        <main className="discover-main">
          <h2 className="discover-heading">Keşfet</h2>
          <div className="discover-posts-container">
            {posts.map(post => (
              <div key={post.post_id} className="discover-post-card">
                <div className="discover-post-header">
                  <img
                    src={getProfileImageUrl(post.avatar)}
                    alt={post.username}
                    className="discover-avatar"
                  />
                  <div className="discover-post-user-info">
                    <div className="discover-user-info-header">
                      <Link
                        to={`/profile/${post.user_id}`}
                        className="discover-author-name"
                      >
                        {post.username}
                      </Link>
                      <span className="discover-username">@{post.username}</span>
                    </div>
                  </div>
                  <div className="discover-post-menu">
                    <button
                      className="discover-menu-button"
                      onClick={() => togglePostMenu(post.post_id)}
                    >
                      <BiDotsHorizontalRounded />
                    </button>
                    {post.showMenu && (
                      <div className="discover-dropdown-menu">
                        {parseInt(post.user_id) === parseInt(currentUserId) && (
                          <button
                            className="discover-dropdown-item"
                            onClick={() => confirmDeletePost(post.post_id)}
                          >
                            <BiTrash /> Sil
                          </button>
                        )}
                        <button
                          className="discover-dropdown-item"
                          onClick={() => handleReportPost(post.post_id)}
                        >
                          <BiFlag /> Şikayet Et
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="discover-post-content">
                  <p>{post.content}</p>
                </div>

                <div className="discover-post-actions">
                  <span className="discover-post-timestamp">
                    {formatTimeAgo(post.created_at)}
                  </span>
                  <div className="discover-interaction-buttons">
                    <button
                      className="discover-action-button"
                      onClick={() => handleLike(post.post_id)}
                    >
                      {post.isLiked ? <FaHeart className="liked" /> : <FaRegHeart />}
                      <span>{post.likes_count ?? 0}</span>
                    </button>
                    <button
                      className="discover-action-button"
                      onClick={() => toggleComments(post.post_id)}
                    >
                      <FaComment />
                      <span>{post.comments.length}</span>
                    </button>
                  </div>
                </div>

                {post.showComments && (
                  <div className="discover-comments-section">
                    {post.comments.map(comment => (
                      <div key={comment.comment_id} className="discover-comment">
                        <div className="discover-comment-header">
                          <img
                            src={getProfileImageUrl(comment.profile_image_url)}
                            alt={comment.username}
                            className="discover-comment-avatar"
                          />
                          <div className="discover-comment-info">
                            <div className="discover-user-info-header">
                              <Link
                                to={`/profile/${comment.user_id}`}
                                className="discover-comment-author"
                              >
                                {comment.username}
                              </Link>
                              <span className="discover-username">@{comment.username}</span>
                            </div>
                            <span className="discover-comment-date">
                              {formatTimeAgo(comment.created_at)}
                            </span>
                          </div>
                          <div className="discover-comment-menu">
                            <button
                              className="discover-menu-button"
                              onClick={() => toggleCommentMenu(post.post_id, comment.comment_id)}
                            >
                              <BiDotsHorizontalRounded />
                            </button>
                            {comment.showMenu && (
                              <div className="discover-dropdown-menu">
                                {parseInt(comment.user_id) === parseInt(currentUserId) && (
                                  <button
                                    className="discover-dropdown-item"
                                    onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}
                                  >
                                    <BiTrash /> Sil
                                  </button>
                                )}
                                <button
                                  className="discover-dropdown-item"
                                  onClick={() => handleReportPost(comment.comment_id)}
                                >
                                  <BiFlag /> Şikayet Et
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="discover-comment-body">
                          <p className="discover-comment-content">{comment.content}</p>
                        </div>
                        <div className="discover-comment-footer">
                          <button
                            className="discover-action-button"
                            onClick={() => handleLike(post.post_id, comment.comment_id)}
                          >
                            {comment.isLiked ? <FaHeart className="liked" /> : <FaRegHeart />}
                            <span>{comment.likes_count ?? 0}</span>
                          </button>
                        </div>
                      </div>
                    ))}
                    <form
                      className="discover-comment-form"
                      onSubmit={e => handleCommentSubmit(post.post_id, e)}
                    >
                      <input
                        type="text"
                        placeholder="Yorumunuzu yazın..."
                        value={post.newComment || ''}
                        onChange={e => handleCommentChange(post.post_id, e.target.value)}
                      />
                      <button type="submit">Gönder</button>
                    </form>
                  </div>
                )}
              </div>
            ))}
          </div>
        </main>

        <aside className="discover-sidebar">
          <div className="discover-sidebar-section">
            <h3>Önerilen Yazarlar</h3>
            {authors.map(author => (
              <div key={author.user_id} className="discover-author-card">
                <div className="discover-author-info">
                  <img
                    src={getProfileImageUrl(author.profile_image_url)}
                    alt={author.username}
                    className="discover-avatar"
                  />
                  <div className="discover-author-details">
                    <Link
                      to={`/profile/${author.user_id}`}
                      className="discover-author-name"
                    >
                      {author.username}
                    </Link>
                    <span className="discover-author-followers">
                      {author.followers_count} takipçi
                    </span>
                  </div>
                </div>
                <button
                  className={`discover-follow-button ${
                    author.is_following ? 'following' : ''
                  }`}
                  onClick={() => handleFollowToggle(author.user_id, author.is_following)}
                >
                  {author.is_following ? 'Takip Ediliyor' : 'Takip Et'}
                </button>
              </div>
            ))}
          </div>

          <div className="discover-sidebar-section">
            <h3>Trend Kitaplar</h3>
            {books.map(book => (
              <div key={book.book_id} className="discover-trending-book">
                <Book className="discover-book-icon" />
                <div className="discover-book-title-author">
                  <Link to={`/book/${book.book_id}`} className="discover-book-title">
                    {book.title}
                  </Link>
                  <span className="discover-book-author">{book.author}</span>
                </div>
                <div className="discover-book-readers">
                  <TrendingUp className="discover-trending-icon" />
                  <span>{book.total_views} okuyucu</span>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* MODALLAR */}
      {modalState.showReportModal && (
        <div className="discover-modal">
          <div className="discover-modal-content">
            <div className="discover-modal-header">
              <h2>Gönderiyi Şikayet Et</h2>
              <span
                className="discover-close"
                onClick={() => setModalState({ ...modalState, showReportModal: false })}
              >
                &times;
              </span>
            </div>
            <div className="discover-modal-body">
              <label>
                Sebep:
                <select value={reportReason} onChange={e => setReportReason(e.target.value)}>
                  <option value="">Bir sebep seçin</option>
                  <option value="İntihar veya kendine zarar verme">İntihar veya kendine zarar verme</option>
                  <option value="Yasa dışı içerik">Yasa dışı içerik</option>
                  <option value="Çıplaklık veya cinsel içerik">Çıplaklık veya cinsel içerik</option>
                  <option value="Nefret söylemi">Nefret söylemi</option>
                  <option value="Şiddet">Şiddet</option>
                  <option value="Taciz">Taciz</option>
                  <option value="Dolandırıcılık veya sahtekarlık">Dolandırıcılık veya sahtekarlık</option>
                  <option value="Fikri mülkiyet ihlali">Fikri mülkiyet ihlali</option>
                  <option value="Diğer">Diğer</option>
                </select>
              </label>
              <label>
                Açıklama:
                <textarea
                  value={reportDescription}
                  onChange={e => setReportDescription(e.target.value)}
                  placeholder="Daha fazla ayrıntı verin"
                />
              </label>
            </div>
            <div className="discover-modal-footer">
              <button className="discover-report-submit" onClick={sendReport}>
                Şikayeti Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {modalState.showDeleteConfirm && (
        <div className="discover-modal">
          <div className="discover-modal-content">
            <div className="discover-modal-header">
              <h2>Gönderiyi Sil</h2>
              <span
                className="discover-close"
                onClick={() => setModalState({ ...modalState, showDeleteConfirm: false })}
              >
                &times;
              </span>
            </div>
            <div className="discover-modal-body">
              <p>Bu gönderiyi silmek istediğinizden emin misiniz?</p>
            </div>
            <div className="discover-modal-footer">
              <button
                className="discover-cancel-submit"
                onClick={() => setModalState({ ...modalState, showDeleteConfirm: false })}
              >
                Vazgeç
              </button>
              <button className="discover-delete-submit" onClick={handleDeletePost}>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {modalState.showSuccessModal && (
        <div className="discover-modal">
          <div className="discover-modal-content">
            <div className="discover-modal-header">
              <h2>Şikayet Alındı</h2>
              <span
                className="discover-close"
                onClick={() => setModalState({ ...modalState, showSuccessModal: false })}
              >
                &times;
              </span>
            </div>
            <div className="discover-modal-body">
              <p>Şikayetiniz başarıyla alındı. Teşekkür ederiz.</p>
            </div>
            <div className="discover-modal-footer">
              <button
                className="discover-report-submit"
                onClick={() => setModalState({ ...modalState, showSuccessModal: false })}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Discover;